import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X,
  Plus,
  Eye,
  Package,
  Calendar,
  DollarSign,
  Loader2,
  Link2,
  FileText,
  ShoppingCart,
  AlertCircle,
  MoreHorizontal,
  Copy,
  ExternalLink,
  Merge,
  Trash2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { formatCurrency, formatDate } from '../lib/utils';
import { ProcurementBatch } from '../types';
import { 
  fetchProcurementBatches, 
  generatePublicProcurementLink,
  mergeProcurementBatches,
  deleteProcurementBatches
} from '../services/procurementService';

interface ProcurementBatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateNewBatch: () => void;
  onViewBatch?: (batch: ProcurementBatch) => void;
}

export function ProcurementBatchesModal({ 
  isOpen, 
  onClose, 
  onCreateNewBatch,
  onViewBatch 
}: ProcurementBatchesModalProps) {
  const [batches, setBatches] = useState<ProcurementBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<ProcurementBatch | null>(null);
  const [showBatchDetails, setShowBatchDetails] = useState(false);
  const [generatingLink, setGeneratingLink] = useState<string | null>(null);
  
  // Selection and bulk operations state
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [mergingBatches, setMergingBatches] = useState(false);
  const [deletingBatches, setDeletingBatches] = useState(false);
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load batches when modal opens
  useEffect(() => {
    const loadBatches = async () => {
      if (!isOpen) return;
      
      try {
        setLoading(true);
        setError(null);
        const batchData = await fetchProcurementBatches();
        setBatches(batchData);
      } catch (error) {
        console.error('Failed to load procurement batches:', error);
        setError('Failed to load procurement batches');
        toast.error('Failed to load procurement batches');
      } finally {
        setLoading(false);
      }
    };

    loadBatches();
  }, [isOpen]);

  const getStatusConfig = (status: ProcurementBatch['status']) => {
    switch (status) {
      case 'draft':
        return {
          label: 'Draft',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: FileText
        };
      case 'generated':
        return {
          label: 'Generated',
          color: 'bg-blue-100 text-blue-800 border-blue-200',
          icon: FileText
        };
      case 'ordered':
        return {
          label: 'Ordered',
          color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          icon: ShoppingCart
        };
      case 'received':
        return {
          label: 'Received',
          color: 'bg-green-100 text-green-800 border-green-200',
          icon: Package
        };
      default:
        return {
          label: 'Unknown',
          color: 'bg-gray-100 text-gray-800 border-gray-200',
          icon: AlertCircle
        };
    }
  };

  const handleCreateNew = () => {
    onCreateNewBatch();
    onClose();
  };

  const handleViewBatch = (batch: ProcurementBatch) => {
    setSelectedBatch(batch);
    setShowBatchDetails(true);
  };

  const handleGenerateLink = async (batch: ProcurementBatch) => {
    try {
      setGeneratingLink(batch.id);
      const publicUrl = await generatePublicProcurementLink(batch.id);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Public link generated and copied to clipboard!');
      
      // Update the batch in state to reflect the new public URL
      setBatches(prev => prev.map(b => 
        b.id === batch.id ? { ...b, publicUrl } : b
      ));
      
      if (selectedBatch?.id === batch.id) {
        setSelectedBatch(prev => prev ? { ...prev, publicUrl } : null);
      }
    } catch (error) {
      console.error('Failed to generate link:', error);
      toast.error('Failed to generate public link');
    } finally {
      setGeneratingLink(null);
    }
  };

  const handleCopyLink = async (publicUrl: string) => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleOpenLink = (publicUrl: string) => {
    window.open(publicUrl, '_blank');
  };

  const handleCloseBatchDetails = () => {
    setShowBatchDetails(false);
    setSelectedBatch(null);
  };

  // Selection handlers
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedBatchIds(new Set());
  };

  const toggleBatchSelection = (batchId: string) => {
    const newSelection = new Set(selectedBatchIds);
    if (newSelection.has(batchId)) {
      newSelection.delete(batchId);
    } else {
      newSelection.add(batchId);
    }
    setSelectedBatchIds(newSelection);
  };

  const selectAllBatches = () => {
    setSelectedBatchIds(new Set(batches.map(b => b.id)));
  };

  const clearSelection = () => {
    setSelectedBatchIds(new Set());
  };

  // Bulk operation handlers
  const handleMergeBatches = async () => {
    if (selectedBatchIds.size < 2) {
      toast.error('Please select at least 2 batches to merge');
      return;
    }

    try {
      setMergingBatches(true);
      // Assuming we have user context - you may need to adjust this
      const newBatchId = await mergeProcurementBatches(
        Array.from(selectedBatchIds),
        'system' // You should replace this with actual user ID
      );
      
      toast.success('Batches merged successfully!');
      
      // Reload batches
      const batchData = await fetchProcurementBatches();
      setBatches(batchData);
      
      // Clear selection and exit selection mode
      setSelectedBatchIds(new Set());
      setSelectionMode(false);
      setShowMergeConfirm(false);
    } catch (error) {
      console.error('Failed to merge batches:', error);
      toast.error('Failed to merge batches');
    } finally {
      setMergingBatches(false);
    }
  };

  const handleDeleteBatches = async () => {
    if (selectedBatchIds.size === 0) {
      toast.error('Please select batches to delete');
      return;
    }

    try {
      setDeletingBatches(true);
      await deleteProcurementBatches(Array.from(selectedBatchIds));
      
      toast.success('Batches deleted successfully!');
      
      // Reload batches
      const batchData = await fetchProcurementBatches();
      setBatches(batchData);
      
      // Clear selection and exit selection mode
      setSelectedBatchIds(new Set());
      setSelectionMode(false);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete batches:', error);
      toast.error('Failed to delete batches');
    } finally {
      setDeletingBatches(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <ShoppingCart className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Procurement Batches
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm mt-1"
                    >
                      Manage bulk purchase orders and procurement batches
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* Batches List */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Previous Batches
                        {selectionMode && selectedBatchIds.size > 0 && (
                          <Badge variant="secondary" className="ml-2">
                            {selectedBatchIds.size} selected
                          </Badge>
                        )}
                      </h3>
                      
                      <div className="flex items-center gap-2">
                        {!selectionMode ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={toggleSelectionMode}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Select Batches
                          </Button>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={selectAllBatches}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={clearSelection}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              Clear
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowMergeConfirm(true)}
                              disabled={selectedBatchIds.size < 2}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Merge className="h-4 w-4 mr-1" />
                              Merge ({selectedBatchIds.size})
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowDeleteConfirm(true)}
                              disabled={selectedBatchIds.size === 0}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete ({selectedBatchIds.size})
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={toggleSelectionMode}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                {error ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Batches</h3>
                    <p className="text-gray-600">{error}</p>
                  </div>
                ) : loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">Loading batches...</span>
                  </div>
                ) : batches.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Package className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Batches Found</h3>
                    <p className="text-gray-600 mb-4">
                      No procurement batches have been created yet.
                    </p>
                    <Button onClick={handleCreateNew} variant="outline" className="hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300">
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Batch
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          {selectionMode && (
                            <TableHead className="w-12">
                              <Checkbox
                                checked={selectedBatchIds.size === batches.length && batches.length > 0}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    selectAllBatches();
                                  } else {
                                    clearSelection();
                                  }
                                }}
                              />
                            </TableHead>
                          )}
                          <TableHead className="font-semibold text-gray-700">Batch Number</TableHead>
                          <TableHead className="font-semibold text-gray-700">Date Created</TableHead>
                          <TableHead className="font-semibold text-gray-700">Orders</TableHead>
                          <TableHead className="font-semibold text-gray-700">Items</TableHead>
                          <TableHead className="font-semibold text-gray-700">Total Cost</TableHead>
                          <TableHead className="font-semibold text-gray-700">Status</TableHead>
                          <TableHead className="font-semibold text-gray-700">Priority Orders</TableHead>
                          <TableHead className="w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {batches.map((batch, index) => {
                          const statusConfig = getStatusConfig(batch.status);
                          const StatusIcon = statusConfig.icon;
                          return (
                            <motion.tr
                              key={batch.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.05 }}
                              className="hover:bg-gray-50 transition-all duration-300 hover:shadow-sm"
                            >
                              {selectionMode && (
                                <TableCell>
                                  <Checkbox
                                    checked={selectedBatchIds.has(batch.id)}
                                    onCheckedChange={() => toggleBatchSelection(batch.id)}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <div className="font-medium text-gray-900">
                                  {batch.batchNumber}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Calendar className="h-4 w-4" />
                                  {formatDate(batch.createdAt)}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="font-medium text-gray-900">
                                  {batch.orderIds.length}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className="font-medium text-gray-900">
                                  {batch.items.length}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 font-semibold text-gray-900">
                                  <DollarSign className="h-4 w-4" />
                                  {formatCurrency(batch.totalCost)}
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={`${statusConfig.color} border-transparent hover:${statusConfig.color} text-[10px] px-1.5 py-0.5 h-5 leading-none`}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {batch.priorityOrdersIncluded ? (
                                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 hover:text-amber-800 text-[10px] px-1.5 py-0.5 h-5 leading-none">
                                    ⚡ Included
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400 text-sm">None</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => handleViewBatch(batch)}
                                    >
                                      <Eye className="w-4 h-4 mr-2" />
                                      View Details
                                    </DropdownMenuItem>
                                    {batch.publicUrl ? (
                                      <>
                                        <DropdownMenuItem
                                          onClick={() => handleCopyLink(batch.publicUrl!)}
                                        >
                                          <Copy className="w-4 h-4 mr-2" />
                                          Copy Link
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          onClick={() => handleOpenLink(batch.publicUrl!)}
                                        >
                                          <ExternalLink className="w-4 h-4 mr-2" />
                                          Open Link
                                        </DropdownMenuItem>
                                      </>
                                    ) : (
                                      <DropdownMenuItem
                                        onClick={() => handleGenerateLink(batch)}
                                        disabled={generatingLink === batch.id}
                                      >
                                        {generatingLink === batch.id ? (
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                          <Link2 className="w-4 h-4 mr-2" />
                                        )}
                                        Generate Link
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </motion.tr>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Package className="h-4 w-4" />
                <span>
                  {batches.length} batch{batches.length !== 1 ? 'es' : ''} • Total procurement history
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="px-6"
                >
                  Close
                </Button>
                <Button
                  onClick={handleCreateNew}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      )}
      
      {/* Batch Details Modal */}
      {showBatchDetails && selectedBatch && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10002] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      Batch Details: {selectedBatch.batchNumber}
                    </h2>
                    <p className="text-white/80 text-sm mt-1">
                      Created on {formatDate(selectedBatch.createdAt)}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleCloseBatchDetails}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {/* Batch Info */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Orders</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedBatch.orderIds.length}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingCart className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Items</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {selectedBatch.items.length}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Total Cost</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(selectedBatch.totalCost)}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-700">Status</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-900">
                      {getStatusConfig(selectedBatch.status).label}
                    </div>
                  </div>
                </div>

                {/* Detailed Items Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Procurement Items
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="font-semibold text-gray-700">Product</TableHead>
                          <TableHead className="font-semibold text-gray-700">Order Quantity</TableHead>
                          <TableHead className="font-semibold text-gray-700">Listing Quantity</TableHead>
                          <TableHead className="font-semibold text-gray-700">Required Quantity</TableHead>
                          <TableHead className="font-semibold text-gray-700">Unit Cost</TableHead>
                          <TableHead className="font-semibold text-gray-700">Line Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedBatch.items.map((item, index) => (
                          <TableRow key={item.productId} className="hover:bg-gray-50">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <img 
                                  src={item.image} 
                                  alt={item.productName}
                                  className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium text-gray-900">
                                    {item.productName}
                                  </span>
                                  {item.brand && (
                                    <span className="text-xs text-gray-500 italic">
                                      {item.brand}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium text-gray-900">
                                {item.orderQuantity || 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium text-gray-900">
                                {item.listingQuantity || 'N/A'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-gray-900">
                                {item.requiredQuantity}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="text-gray-600">
                                {formatCurrency(item.unitCost)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-gray-900">
                                {formatCurrency(item.lineTotal)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <FileText className="h-4 w-4" />
                  <span>Batch Total: {formatCurrency(selectedBatch.totalCost)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCloseBatchDetails}
                    className="px-6"
                  >
                    Close
                  </Button>
                  {selectedBatch.publicUrl ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleCopyLink(selectedBatch.publicUrl!)}
                        className="px-6"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Link
                      </Button>
                      <Button
                        onClick={() => handleOpenLink(selectedBatch.publicUrl!)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open Link
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => handleGenerateLink(selectedBatch)}
                      disabled={generatingLink === selectedBatch.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                    >
                      {generatingLink === selectedBatch.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Link2 className="h-4 w-4 mr-2" />
                          Generate Link
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Merge Confirmation Dialog */}
      {showMergeConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10002] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Merge className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Merge Batches</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to merge {selectedBatchIds.size} procurement batches? 
              This will create a new batch and delete the selected ones. This action cannot be undone.
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMergeConfirm(false)}
                disabled={mergingBatches}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMergeBatches}
                disabled={mergingBatches}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {mergingBatches ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Merging...
                  </>
                ) : (
                  <>
                    <Merge className="w-4 h-4 mr-2" />
                    Merge Batches
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10002] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Batches</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {selectedBatchIds.size} procurement batches? 
              This will also remove procurement flags from associated orders. This action cannot be undone.
            </p>
            
            <div className="flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingBatches}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteBatches}
                disabled={deletingBatches}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deletingBatches ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Batches
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}